import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Icon from '../components/Icon';
import {
  fetchPitchAccessCodes,
  createPitchAccessCode,
  fetchPitchAccessCodeDetail,
  revokePitchAccessCode,
  fetchInvestorsList,
} from '../services/api';
import { timeAgo } from '../utils/format';

const STATUS_BADGE = {
  active:  { class: 'badge-success', label: 'Active'  },
  expired: { class: 'badge-warning', label: 'Expired' },
  revoked: { class: 'badge-danger',  label: 'Revoked' },
};

// Inline tag shown next to the status pill for permanent admin codes so
// they're visually distinct from time-limited investor codes.
const AdminBadge = () => (
  <span className="badge badge-info" style={{ marginLeft: 6, letterSpacing: '0.04em' }}>Admin</span>
);

const PITCH_URL = 'https://pitch.coynitt.ca/';
const MST_TZ = 'America/Edmonton'; // MST/MDT canonical zone

// MST-formatted absolute timestamp. Edmonton is the canonical IANA zone
// for Mountain Time (handles MST/MDT switchover automatically).
const fmtMST = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: MST_TZ,
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
};

const PitchAccess = () => {
  const [codes, setCodes] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [query, setQuery] = useState('');
  const [selectedCode, setSelectedCode] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showGenerator, setShowGenerator] = useState(false);
  const [busy, setBusy] = useState(false);
  const [justCreated, setJustCreated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, inv] = await Promise.all([
        fetchPitchAccessCodes(),
        fetchInvestorsList().catch(() => ({ data: [] })),
      ]);
      setCodes(c?.data || []);
      setInvestors(inv?.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load pitch codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (code) => {
    setSelectedCode(code);
    setDetail(null);
    if (!code) return;
    try {
      const d = await fetchPitchAccessCodeDetail(code.code);
      setDetail(d);
    } catch (err) {
      setError(err.message || 'Failed to load detail');
    }
  }, []);

  const counts = useMemo(() => ({
    active:  codes.filter((c) => c.status === 'active').length,
    expired: codes.filter((c) => c.status === 'expired').length,
    revoked: codes.filter((c) => c.status === 'revoked').length,
    all:     codes.length,
  }), [codes]);

  const totalAccesses = useMemo(
    () => codes.reduce((s, c) => s + (c.access_count || 0), 0),
    [codes]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return codes.filter((c) => {
      if (activeTab !== 'all' && c.status !== activeTab) return false;
      if (!q) return true;
      return (
        (c.code || '').toLowerCase().includes(q) ||
        (c.investor_name  || '').toLowerCase().includes(q) ||
        (c.investor_email || '').toLowerCase().includes(q) ||
        (c.investor_contact || '').toLowerCase().includes(q) ||
        (c.label || '').toLowerCase().includes(q)
      );
    });
  }, [codes, activeTab, query]);

  const handleGenerate = async (form) => {
    setBusy(true);
    setError(null);
    try {
      const res = await createPitchAccessCode(form);
      setJustCreated(res);
      setShowGenerator(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Generation failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!selectedCode) return;
    if (!window.confirm(`Revoke ${selectedCode.code}? The recipient will lose access immediately.`)) return;
    const reason = window.prompt('Optional revoke reason (audit log):') || null;
    setBusy(true);
    try {
      await revokePitchAccessCode(selectedCode.code, reason);
      await load();
      await loadDetail({ ...selectedCode, status: 'revoked' });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Pitch Access</h1>
          <p>
            Generate unique access codes for <a href={PITCH_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>pitch.coynitt.ca</a> · view who's seen the deck, when, and from where. All codes are valid for ≤ 30 days.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => { setShowGenerator(true); setJustCreated(null); }}>
          <Icon name="shield" size={14} /> Generate access code
        </button>
      </div>

      {error && (
        <div role="alert" style={errStyle}>{error}</div>
      )}

      {justCreated && (
        <div className="card mb-4" style={{ borderColor: 'var(--teal)', borderWidth: 2, background: 'var(--bg-elevated)' }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="card-title" style={{ color: 'var(--teal)' }}>Code generated</div>
              <div className="text-tx3 text-xs" style={{ marginBottom: 8 }}>
                Copy this now — for security it'll be displayed alongside other codes after refresh, but having it on a clean banner makes copy-paste reliable.
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 22, fontWeight: 800, color: 'var(--tx1)', letterSpacing: '0.05em' }}>
                {justCreated.code}
              </div>
              <div className="text-tx3 text-xs mt-2">
                Expires {fmtMST(justCreated.expires_at)} · valid {daysLeft(justCreated.expires_at)} days
              </div>
            </div>
            <div className="row row-gap-2">
              <button type="button" className="btn btn-outline" onClick={() => navigator.clipboard?.writeText(justCreated.code)}>
                Copy code
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setJustCreated(null)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary stats */}
      <div className="dashboard-grid stagger mb-4">
        <div className="card"><div className="card-title">Active codes</div>
          <div className="stat-value" style={{ color: 'var(--ok)' }}>{counts.active}</div>
          <div className="stat-label">Currently valid</div></div>
        <div className="card"><div className="card-title">Expired</div>
          <div className="stat-value" style={{ color: 'var(--warn)' }}>{counts.expired}</div>
          <div className="stat-label">Past their 30-day window</div></div>
        <div className="card"><div className="card-title">Revoked</div>
          <div className="stat-value" style={{ color: 'var(--err)' }}>{counts.revoked}</div>
          <div className="stat-label">Manually revoked</div></div>
        <div className="card"><div className="card-title">Total deck views</div>
          <div className="stat-value">{totalAccesses}</div>
          <div className="stat-label">Across all codes</div></div>
      </div>

      <div className="card">
        {/* Tab bar */}
        <div className="row" data-tabs="true" style={{ gap: 4, borderBottom: '1px solid var(--bd)', marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { id: 'active',  label: 'Active'  },
            { id: 'expired', label: 'Expired' },
            { id: 'revoked', label: 'Revoked' },
            { id: 'all',     label: 'All'     },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => { setActiveTab(t.id); setSelectedCode(null); setDetail(null); }}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                color: activeTab === t.id ? 'var(--teal)' : 'var(--tx2)',
                fontWeight: activeTab === t.id ? 700 : 500,
                fontSize: 13,
                borderBottom: activeTab === t.id ? '2px solid var(--teal)' : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
              }}
            >
              {t.label}
              <span className="badge badge-muted" style={{ marginLeft: 8 }}>{counts[t.id]}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="row mb-3" style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx3)' }}>
            <Icon name="search" size={16} />
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search code, investor name, email, or label..."
            aria-label="Search pitch access codes"
            style={{
              width: '100%',
              padding: '10px 14px 10px 36px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--r-md)',
              color: 'var(--tx1)',
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>

        <div className="row row-gap-4" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Table */}
          <div style={{ flex: '1 1 580px', minWidth: 280 }}>
            {loading && codes.length === 0 ? (
              [0, 1, 2, 3].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 56, marginBottom: 8 }} />)
            ) : filtered.length === 0 ? (
              <div className="empty">
                <div className="empty-icon"><Icon name="shield" size={26} /></div>
                <h3>No codes in {activeTab}</h3>
                <p>{codes.length === 0 ? 'Generate your first access code to share the pitch deck with an investor.' : 'Try clearing search or switching tabs.'}</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Recipient</th>
                      <th>Last viewed (MST)</th>
                      <th>Views</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c) => {
                      const sb = STATUS_BADGE[c.status] || STATUS_BADGE.active;
                      const isSelected = selectedCode?.id === c.id;
                      return (
                        <tr
                          key={c.id}
                          onClick={() => loadDetail(c)}
                          style={{ cursor: 'pointer', background: isSelected ? 'var(--teal-faint)' : 'transparent' }}
                        >
                          <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--tx1)', fontWeight: 600 }}>{c.code}</td>
                          <td>
                            <div style={{ color: 'var(--tx1)', fontWeight: 600, fontSize: 13 }}>
                              {c.investor_name || c.recipient_name || c.label || '—'}
                            </div>
                            {c.investor_email && (
                              <div className="text-tx3 text-xs">{c.investor_email}</div>
                            )}
                          </td>
                          <td>
                            {c.last_accessed_at ? (
                              <div>
                                <div style={{ color: 'var(--tx1)', fontSize: 12 }}>{fmtMST(c.last_accessed_at)}</div>
                                <div className="text-tx3 text-xs">{timeAgo(c.last_accessed_at)}</div>
                              </div>
                            ) : (
                              <span className="text-tx3 text-xs">Never</span>
                            )}
                          </td>
                          <td style={{ color: 'var(--tx1)', fontWeight: 600 }}>{c.access_count || 0}</td>
                          <td>
                            <span className={clsx('badge', sb.class)}>{sb.label}</span>
                            {c.is_admin_bypass && <AdminBadge />}
                            {c.status === 'active' && !c.is_admin_bypass && (
                              <div className="text-tx3 text-xs">{daysLeft(c.expires_at)}d left</div>
                            )}
                            {c.is_admin_bypass && (
                              <div className="text-tx3 text-xs">Never expires</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="card col col-gap-3" style={{ flex: '1 1 380px', minWidth: 320, padding: 'var(--s5)', background: 'var(--bg-elevated)' }}>
            {!selectedCode ? (
              <div className="empty">
                <div className="empty-icon"><Icon name="shield" size={26} /></div>
                <h3>Select a code</h3>
                <p>Click any row to see the access log and revoke if needed.</p>
              </div>
            ) : (
              <>
                <div>
                  <div className="text-tx3 text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Code</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 800, color: 'var(--tx1)', wordBreak: 'break-all' }}>{selectedCode.code}</div>
                  <button type="button" className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 11, marginTop: 4 }} onClick={() => navigator.clipboard?.writeText(selectedCode.code)}>
                    Copy
                  </button>
                </div>

                <div className="row row-gap-3" style={{ flexWrap: 'wrap' }}>
                  <DetailKV label="Status" value={(STATUS_BADGE[selectedCode.status] || STATUS_BADGE.active).label} />
                  <DetailKV label="Recipient" value={selectedCode.investor_name || selectedCode.recipient_name || '—'} />
                  <DetailKV label="Granted" value={fmtMST(selectedCode.granted_at)} />
                  <DetailKV label="Expires" value={fmtMST(selectedCode.expires_at)} />
                  <DetailKV label="Views" value={selectedCode.access_count || 0} />
                  <DetailKV label="First viewed" value={selectedCode.first_accessed_at ? fmtMST(selectedCode.first_accessed_at) : 'Never'} />
                </div>

                {selectedCode.notes && (
                  <div>
                    <div className="text-tx3 text-xs">Notes</div>
                    <div style={{ color: 'var(--tx2)', fontSize: 13 }}>{selectedCode.notes}</div>
                  </div>
                )}

                {selectedCode.status === 'active' && (
                  <button type="button" className="btn btn-outline" style={{ padding: '8px 14px', fontSize: 12, borderColor: 'var(--err)', color: 'var(--err)' }} disabled={busy} onClick={handleRevoke}>
                    Revoke access
                  </button>
                )}

                <div className="card-divider" />

                <div>
                  <div className="text-tx3 text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 8 }}>
                    Access log ({detail?.log?.length || 0})
                  </div>
                  {!detail ? (
                    <div className="skeleton skeleton-block" style={{ height: 100 }} />
                  ) : detail.log.length === 0 ? (
                    <div className="text-tx3 text-sm">No access attempts yet.</div>
                  ) : (
                    <div style={{ maxHeight: 280, overflowY: 'auto', display: 'grid', gap: 6 }}>
                      {detail.log.map((entry) => (
                        <div key={entry.id} style={{ background: 'var(--bg-card)', borderRadius: 'var(--r-sm)', padding: 8, fontSize: 12 }}>
                          <div className="row row-gap-2" style={{ justifyContent: 'space-between' }}>
                            <span className={clsx('badge', entry.success ? 'badge-success' : 'badge-danger')}>
                              {entry.success ? 'view' : (entry.failure_reason || 'fail')}
                            </span>
                            <span className="text-tx3 text-xs">{fmtMST(entry.accessed_at)}</span>
                          </div>
                          <div className="text-tx3 text-xs" style={{ marginTop: 4 }}>
                            {entry.ip || 'unknown ip'} · {(entry.user_agent || '').slice(0, 80)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showGenerator && (
        <GeneratorModal
          investors={investors}
          busy={busy}
          onCancel={() => setShowGenerator(false)}
          onSubmit={handleGenerate}
        />
      )}
    </>
  );
};

const DetailKV = ({ label, value }) => (
  <div className="col col-gap-1" style={{ minWidth: 140 }}>
    <div className="text-tx3 text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 12, color: 'var(--tx1)' }}>{value}</div>
  </div>
);

const GeneratorModal = ({ investors, busy, onCancel, onSubmit }) => {
  const [investorId, setInvestorId] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [label, setLabel] = useState('');
  const [ttlDays, setTtlDays] = useState(30);
  const [notes, setNotes] = useState('');

  const onPickInvestor = (id) => {
    setInvestorId(id);
    const inv = investors.find((i) => i.id === id);
    if (inv) {
      setRecipientName(inv.primary_contact_name || '');
      setRecipientEmail(inv.email || '');
      setLabel(`${inv.name}${inv.primary_contact_name ? ` — ${inv.primary_contact_name}` : ''}`);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      investor_id: investorId || null,
      recipient_name: recipientName,
      recipient_email: recipientEmail,
      label,
      ttl_days: ttlDays,
      notes,
    });
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onCancel} style={overlayStyle}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="card" style={modalStyle}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--tx1)', marginBottom: 'var(--s2)' }}>Generate access code</h2>
        <p style={{ fontSize: 13, color: 'var(--tx2)', marginBottom: 'var(--s4)' }}>
          Codes are valid for up to <strong>30 days</strong>. Share the code via email; the recipient enters it at pitch.coynitt.ca to view the deck.
        </p>

        <div className="col col-gap-3">
          <Field label="Investor (optional — pick from CRM)">
            <select value={investorId} onChange={(e) => onPickInvestor(e.target.value)} style={fieldStyle}>
              <option value="">— No CRM link (manual entry below) —</option>
              {(investors || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((i) => (
                <option key={i.id} value={i.id}>{i.name}{i.primary_contact_name ? ` · ${i.primary_contact_name}` : ''}</option>
              ))}
            </select>
          </Field>

          <Field label="Recipient name">
            <input type="text" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} style={fieldStyle} placeholder="e.g. Sean Judge" />
          </Field>

          <Field label="Recipient email">
            <input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} style={fieldStyle} placeholder="e.g. sean@castleisland.vc" />
          </Field>

          <Field label="Internal label (shown to admin only)">
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} style={fieldStyle} placeholder="e.g. Castle Island — Sean Judge — May seed round" />
          </Field>

          <Field label="Valid for (days, max 30)">
            <input
              type="number" min="1" max="30"
              value={ttlDays}
              onChange={(e) => setTtlDays(Math.max(1, Math.min(30, parseInt(e.target.value || '30', 10))))}
              style={fieldStyle}
            />
          </Field>

          <Field label="Notes (optional)">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ ...fieldStyle, resize: 'vertical' }} placeholder="Anything to remember about this share" />
          </Field>
        </div>

        <div className="row row-gap-2 mt-4" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || (!investorId && !recipientEmail.trim())}>
            {busy ? 'Generating…' : 'Generate code'}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="col col-gap-2">
    <span className="text-tx3 text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{label}</span>
    {children}
  </label>
);

const fieldStyle = {
  padding: '10px 14px',
  background: 'var(--bg-elevated)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-md)',
  color: 'var(--tx1)',
  fontSize: 13,
  outline: 'none',
  fontFamily: 'var(--font)',
};

const errStyle = {
  background: 'var(--err-bg)',
  color: 'var(--err)',
  padding: 'var(--s3)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
  marginBottom: 'var(--s3)',
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'var(--bg-overlay)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 1000,
  padding: 'var(--s4)',
};

const modalStyle = {
  maxWidth: 520,
  width: '100%',
  padding: 'var(--s6)',
  animation: 'scaleIn var(--tb2) var(--spring-sm)',
};

export default PitchAccess;
